/**
 * The equipment-type vocabulary — the SPA's mirror of DEVICE_KIND_LABELS in
 * controller/models/device.py.
 *
 * These are the only values `Device.kind` can ever hold: the backend derives the kind from a
 * device's model/hostname strings (derive_device_kind) and always returns one of these keys,
 * falling back to "other" for hardware it does not recognise. The controller is the source of
 * truth; this file exists only so a <select> can render the labels without a round trip.
 *
 * WHY THE FULL LIST AND NOT "THE KINDS PRESENT IN THIS FLEET". The per-kind rollup endpoint
 * (/device-summary) could supply the options, but then the filter's own contents would change
 * as devices come and go — "Firewall" disappearing from the menu on the day the only firewall
 * is decommissioned reads as a broken page, not as an empty result. A fixed vocabulary makes
 * "Firewalls → 0 rows" an ANSWER rather than a missing control, which is the whole complaint
 * this filter exists to fix.
 *
 * Ordered by how often an operator reaches for them, with "Other" last — it is the fallback
 * bucket for unrecognised hardware, not a category anyone goes looking for first.
 */

/** One selectable equipment type: the API value plus its human label. */
export interface DeviceKind {
  /** The value sent as ?kind= and compared against `Device.kind`. */
  kind: string;
  label: string;
}

export const DEVICE_KINDS: readonly DeviceKind[] = [
  { kind: "access_point", label: "Access Point" },
  { kind: "switch", label: "Switch" },
  { kind: "gateway", label: "Gateway" },
  { kind: "firewall", label: "Firewall" },
  { kind: "wireless_controller", label: "Wireless Controller" },
  { kind: "other", label: "Other" },
];

/** Human label for a kind key; falls back to the raw key so an unknown value is still legible. */
export function deviceKindLabel(kind: string | undefined | null): string {
  if (!kind) return "";
  return DEVICE_KINDS.find((k) => k.kind === kind)?.label ?? kind;
}

/** Plural noun for a kind ("gateways", "access points"); "devices" when no kind is set. */
export function deviceKindPlural(kind: string | undefined | null): string {
  if (!kind) return "devices";
  const label = deviceKindLabel(kind).toLowerCase();
  return /(s|sh|ch|x)$/.test(label) ? `${label}es` : `${label}s`;
}

/**
 * The "Needs AP baseline" filter is about access points only (OP#1775). It showed on the
 * Switches and Gateways lists too, where it can only empty the table (owner images 18, 23).
 */
export function showApBaselineFilter(kind: string | undefined | null): boolean {
  return !kind || kind === "access_point";
}

/**
 * Empty-state text for the device list. With NO filter set, "No devices match the current
 * filters." was false (owner image 23, Gateways): say what is missing and where to add one.
 */
export function devicesEmptyText(opts: {
  search: string;
  anyFilter: boolean;
  kind: string | undefined | null;
  atSite: boolean;
}): string {
  const what = deviceKindPlural(opts.kind);
  if (opts.search) {
    return `No device matches “${opts.search}”. Searched serial, name, model, MAC, IP and notes across ${
      opts.kind ? what : "the fleet"
    } in scope.`;
  }
  if (opts.anyFilter) return "No devices match the current filters.";
  return `No ${what} ${opts.atSite ? "at this site" : "in scope"} yet. Add one from Onboard.`;
}
